import {
  useState,
  useRef,
  useEffect,
  useCallback,
  type ForwardedRef,
} from 'react';
import {
  readReliableDuration,
  decodeDurationSecondsFromUrl,
  getAudioLoadError,
  type AudioLoadError,
} from '../voice/audioDuration';
import { formatMmSs } from '../voice/recording';

export interface UseAudioPlayerOptions {
  src: string | undefined;
  onPlayStateChange?: (isPlaying: boolean) => void;
  onProgressChange?: (currentTime: number, duration: number) => void;
  onVolumeChangeProp?: (volume: number) => void;
}

export function useAudioPlayer(
  ref: ForwardedRef<HTMLAudioElement>,
  {
    src,
    onPlayStateChange,
    onProgressChange,
    onVolumeChangeProp,
  }: UseAudioPlayerOptions
) {
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(1);
  const [isMuted, setIsMuted] = useState(false);
  const [isDurationLoading, setIsDurationLoading] = useState(false);
  const [loadError, setLoadError] = useState<AudioLoadError | null>(null);
  const audioRef = useRef<HTMLAudioElement>(null);
  const durationProbeRef = useRef<AbortController | null>(null);
  const probedSrcRef = useRef<string | undefined>(undefined);

  const stopDurationProbe = useCallback(() => {
    durationProbeRef.current?.abort();
    durationProbeRef.current = null;
  }, []);

  // Decoding downloads the whole file, so it runs at most once per source and
  // only until the element itself reports a reliable duration.
  const startDurationProbe = useCallback((url: string) => {
    if (probedSrcRef.current === url) {
      return;
    }
    probedSrcRef.current = url;
    const controller = new AbortController();
    durationProbeRef.current = controller;

    decodeDurationSecondsFromUrl(url, controller.signal)
      .then(decoded => {
        if (decoded !== null && !controller.signal.aborted) {
          setDuration(prev => (prev > 0 ? prev : decoded));
        }
      })
      .catch(() => {
        // abort / CORS / декодирование
      })
      .finally(() => {
        if (!controller.signal.aborted) {
          setIsDurationLoading(false);
        }
        if (durationProbeRef.current === controller) {
          durationProbeRef.current = null;
        }
      });
  }, []);

  const combinedRef = useCallback(
    (node: HTMLAudioElement | null) => {
      audioRef.current = node;
      if (typeof ref === 'function') {
        ref(node);
      } else if (ref) {
        ref.current = node;
      }
    },
    [ref]
  );

  useEffect(() => {
    stopDurationProbe();
    probedSrcRef.current = undefined;
    setDuration(0);
    setCurrentTime(0);
    setLoadError(null);
    const el = audioRef.current;
    if (el && src) {
      el.load();
    }

    if (!src) {
      setIsDurationLoading(false);
      return;
    }

    setIsDurationLoading(true);
    // iOS loads no metadata before playback, so the probe starts right away.
    startDurationProbe(src);

    return stopDurationProbe;
  }, [src, startDurationProbe, stopDurationProbe]);

  useEffect(() => {
    if (duration > 0) {
      setIsDurationLoading(false);
    }
  }, [duration]);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) {
      return;
    }

    const applyDurationFromElement = () => {
      const next = readReliableDuration(audio);
      if (next > 0) {
        stopDurationProbe();
        setDuration(prev => (Math.abs(prev - next) < 0.05 ? prev : next));
      }
    };

    const handleTimeUpdate = () => {
      applyDurationFromElement();
      setCurrentTime(audio.currentTime);
      const d = readReliableDuration(audio);
      onProgressChange?.(
        audio.currentTime,
        d > 0 ? d : Number.isFinite(audio.duration) ? audio.duration : 0
      );
    };

    const handleLoadedMetadata = () => {
      applyDurationFromElement();
      setCurrentTime(audio.currentTime);
    };

    const handleDurationChange = () => {
      applyDurationFromElement();
    };

    const handleProgress = () => {
      applyDurationFromElement();
    };

    const handleLoadedData = () => {
      applyDurationFromElement();
    };

    const handleCanPlay = () => {
      applyDurationFromElement();
    };

    const handleCanPlayThrough = () => {
      applyDurationFromElement();
      if (src && readReliableDuration(audio) <= 0) {
        startDurationProbe(src);
      }
    };

    const handlePlay = () => {
      setIsPlaying(true);
      onPlayStateChange?.(true);
    };

    const handlePause = () => {
      setIsPlaying(false);
      onPlayStateChange?.(false);
    };

    const handleAudioVolumeChange = () => {
      setVolume(audio.volume);
      setIsMuted(audio.muted);
      onVolumeChangeProp?.(audio.volume);
    };

    const handleError = () => {
      stopDurationProbe();
      setIsDurationLoading(false);
      setLoadError(getAudioLoadError(audio.error));
    };

    audio.addEventListener('timeupdate', handleTimeUpdate);
    audio.addEventListener('loadedmetadata', handleLoadedMetadata);
    audio.addEventListener('loadeddata', handleLoadedData);
    audio.addEventListener('canplay', handleCanPlay);
    audio.addEventListener('canplaythrough', handleCanPlayThrough);
    audio.addEventListener('durationchange', handleDurationChange);
    audio.addEventListener('progress', handleProgress);
    audio.addEventListener('play', handlePlay);
    audio.addEventListener('pause', handlePause);
    audio.addEventListener('volumechange', handleAudioVolumeChange);
    audio.addEventListener('error', handleError);

    return () => {
      audio.removeEventListener('timeupdate', handleTimeUpdate);
      audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
      audio.removeEventListener('loadeddata', handleLoadedData);
      audio.removeEventListener('canplay', handleCanPlay);
      audio.removeEventListener('canplaythrough', handleCanPlayThrough);
      audio.removeEventListener('durationchange', handleDurationChange);
      audio.removeEventListener('progress', handleProgress);
      audio.removeEventListener('play', handlePlay);
      audio.removeEventListener('pause', handlePause);
      audio.removeEventListener('volumechange', handleAudioVolumeChange);
      audio.removeEventListener('error', handleError);
    };
  }, [
    onPlayStateChange,
    onProgressChange,
    onVolumeChangeProp,
    src,
    startDurationProbe,
    stopDurationProbe,
  ]);

  const togglePlayPause = useCallback(() => {
    const audio = audioRef.current;
    if (!audio) {
      return;
    }

    if (!audio.paused) {
      audio.pause();
    } else {
      void audio.play().catch(() => setIsPlaying(false));
    }
  }, []);

  const onProgressSliderChange = useCallback(
    (value: number) => {
      const audio = audioRef.current;
      if (!audio) {
        return;
      }

      const d = readReliableDuration(audio) || duration;
      if (d <= 0) {
        return;
      }

      const newTime = (value / 100) * d;
      audio.currentTime = newTime;
      setCurrentTime(newTime);
    },
    [duration]
  );

  const onVolumeSliderChange = useCallback((value: number) => {
    const audio = audioRef.current;
    if (!audio) {
      return;
    }

    audio.volume = value / 100;
    setVolume(value / 100);
    // Raising the volume is a request to hear the audio again.
    if (value > 0 && audio.muted) {
      audio.muted = false;
      setIsMuted(false);
    }
  }, []);

  const toggleMute = useCallback(() => {
    const audio = audioRef.current;
    if (!audio) {
      return;
    }

    audio.muted = !isMuted;
    setIsMuted(!isMuted);
  }, [isMuted]);

  const progressPercent = duration > 0 ? (currentTime / duration) * 100 : 0;
  const playbackCurrentLabel = formatMmSs(Math.floor(currentTime * 1000));
  const playbackTotalLabel =
    duration > 0 ? formatMmSs(Math.floor(duration * 1000)) : '—:—';

  return {
    combinedRef,
    isPlaying,
    isDurationLoading,
    loadError,
    volume,
    isMuted,
    progressPercent,
    playbackCurrentLabel,
    playbackTotalLabel,
    togglePlayPause,
    onProgressSliderChange,
    onVolumeSliderChange,
    toggleMute,
  };
}
